import { useState, useEffect } from 'react';
import axios from 'axios';
import { TrendingUp, DollarSign, Users } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

interface UserGrowthData {
  date: string;
  new_users: number;
  total_users: number;
}

interface RevenueData {
  month: string;
  standard_revenue: number;
  premium_revenue: number;
  total_revenue: number;
}

const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl p-5 backdrop-blur-sm";
const CHART_TOOLTIP = { background: 'rgba(17,17,19,0.95)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '10px', color: '#fff' };

function AnalyticsPage() {
  const [userGrowth, setUserGrowth] = useState<UserGrowthData[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('30');

  useEffect(() => { fetchAnalytics(); }, [selectedPeriod]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const [growthRes, revenueRes] = await Promise.all([
        axios.get<UserGrowthData[]>(`${API_URL}/api/admin/analytics/user-growth`, {
          headers: { Authorization: `Bearer ${token}` }, params: { days: selectedPeriod }
        }),
        axios.get<RevenueData[]>(`${API_URL}/api/admin/analytics/revenue`, {
          headers: { Authorization: `Bearer ${token}` }, params: { months: 12 }
        })
      ]);
      setUserGrowth(growthRes.data);
      setRevenueData(revenueRes.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalRevenue = revenueData.reduce((sum, item) => sum + item.total_revenue, 0);
  const avgRevenue = revenueData.length > 0 ? totalRevenue / revenueData.length : 0;
  const latestMonthRevenue = revenueData[revenueData.length - 1]?.total_revenue || 0;
  const totalNewUsers = userGrowth.reduce((sum, item) => sum + item.new_users, 0);
  const currentTotalUsers = userGrowth[userGrowth.length - 1]?.total_users || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <div className="w-7 h-7 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
          <p className="text-white/40 text-sm">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const summaryCards = [
    { label: 'Total Users', value: currentTotalUsers.toLocaleString(), sub: `+${totalNewUsers} new users`, icon: <Users size={18} />, color: '#007AFF' },
    { label: 'Total Revenue (12mo)', value: `$${totalRevenue.toFixed(2)}`, sub: `Avg: $${avgRevenue.toFixed(2)}/mo`, icon: <DollarSign size={18} />, color: '#34C759' },
    { label: 'Latest Month MRR', value: `$${latestMonthRevenue.toFixed(2)}`, sub: 'Monthly Recurring Revenue', icon: <TrendingUp size={18} />, color: '#FF9F0A' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Analytics & Insights</h1>
          <p className="text-white/40 text-sm mt-1">Track growth, revenue, and user behavior</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-white/40 text-sm">Period:</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-primary/50"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {summaryCards.map((card, i) => (
          <div key={i} className={CARD}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${card.color}20`, color: card.color }}>
                {card.icon}
              </div>
              <div>
                <p className="text-white/40 text-xs mb-1">{card.label}</p>
                <p className="text-2xl font-bold text-white">{card.value}</p>
                <p className="text-green text-xs mt-1">{card.sub}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* User Growth Chart */}
      <div className={CARD}>
        <h2 className="text-base font-semibold text-white mb-1">User Growth Over Time</h2>
        <p className="text-white/40 text-xs mb-4">New user registrations and total user count</p>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={userGrowth}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="date" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }} />
            <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }} />
            <Tooltip contentStyle={CHART_TOOLTIP} />
            <Legend />
            <Line type="monotone" dataKey="new_users" stroke="#007AFF" strokeWidth={2} name="New Users" dot={{ fill: '#007AFF', r: 3 }} />
            <Line type="monotone" dataKey="total_users" stroke="#34C759" strokeWidth={2} name="Total Users" dot={{ fill: '#34C759', r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Revenue Chart */}
      <div className={CARD}>
        <h2 className="text-base font-semibold text-white mb-1">Revenue by Subscription Tier</h2>
        <p className="text-white/40 text-xs mb-4">Monthly revenue breakdown by subscription level</p>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="month" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }} />
            <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }} />
            <Tooltip contentStyle={CHART_TOOLTIP} formatter={(value: number | undefined) => `$${(value || 0).toFixed(2)}`} />
            <Legend />
            <Bar dataKey="standard_revenue" fill="#007AFF" name="Standard ($6.99)" radius={[6, 6, 0, 0]} />
            <Bar dataKey="premium_revenue" fill="#BF5AF2" name="Premium ($9.99)" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Revenue Pie */}
      <div className={CARD}>
        <h2 className="text-base font-semibold text-white mb-1">Revenue Distribution</h2>
        <p className="text-white/40 text-xs mb-4">Total revenue split by subscription tier</p>
        <ResponsiveContainer width="100%" height={320}>
          <PieChart>
            <Pie
              data={[
                { name: 'Standard', value: revenueData.reduce((sum, item) => sum + item.standard_revenue, 0) },
                { name: 'Premium', value: revenueData.reduce((sum, item) => sum + item.premium_revenue, 0) }
              ]}
              cx="50%" cy="50%"
              labelLine={false}
              label={({ name, value, percent }) => `${name}: $${(value as number).toFixed(2)} (${((percent as number) * 100).toFixed(1)}%)`}
              outerRadius={110}
              dataKey="value"
            >
              <Cell fill="#007AFF" />
              <Cell fill="#BF5AF2" />
            </Pie>
            <Tooltip contentStyle={CHART_TOOLTIP} formatter={(value: number | undefined) => `$${(value || 0).toFixed(2)}`} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default AnalyticsPage;
